// src/exercises/spacyMarkdown.js
// ══════════════════════════════════════════════════════════════════════════════
// 📐 EL MARKDOWN DE SPACY — normalizar el texto y partirlo en bloques.
//
// Estas dos funciones vivían dentro de SpacyMessageRenderer.jsx. Están aquí,
// aparte, por dos razones:
//
//   1. Son lógica pura, sin JSX ni React. Se pueden probar con `node` a secas
//      (`test_spacy_renderer.mjs`), sin montar un DOM ni compilar nada. Y falta
//      hacía: el fallo que motivó este archivo —los saltos de línea que se
//      perdían— vivió meses sin que nadie lo viera, porque no había forma
//      barata de mirar qué salía de preProcess.
//   2. Exportarlas desde el .jsx rompía el fast refresh de Vite (un archivo que
//      exporta componentes no puede exportar además otras cosas).
// ══════════════════════════════════════════════════════════════════════════════

// ─────────────────────────────────────────────────────────────────────────────
// PRE-PROCESADOR — Limpia el texto antes de renderizar
// ─────────────────────────────────────────────────────────────────────────────
export function preProcess(text) {
    if (!text) return '';
    return text
        // ⚠️ AQUÍ ESTABA EL FALLO.
        //
        // Antes esto eran DOS reglas sueltas:
        //     .replace(/\*\*\s+/g, '**')
        //     .replace(/\s+\*\*/g, '**')
        //
        // `\s` incluye el salto de línea, así que un mensaje perfectamente
        // formado como:
        //
        //     …simple rule.
        //
        //     **Rule #1: We use the verb *to be*…**
        //
        //     Look at these examples:
        //
        // se convertía en `…simple rule.**Rule #1: …**Look at these examples:`
        // — sin saltos, con los asteriscos pegados a la palabra anterior y el
        // bold roto. El modelo mandaba el texto bien; lo destrozaba esta línea.
        //
        // La regla correcta trabaja sobre el PAR completo y sólo limpia el
        // relleno horizontal de dentro: "** word **" → "**word**", sin tocar
        // nada de fuera.
        .replace(/\*\*[ \t]*([^*\n]+?)[ \t]*\*\*/g, '**$1**')
        // Asegurar espacio DESPUÉS del bold si le sigue una letra: **word**next → **word** next
        .replace(/\*\*([^*\n]+)\*\*([a-zA-ZáéíóúÁÉÍÓÚñÑ])/g, '**$1** $2')
        // …y ANTES, si viene pegado a una palabra: about**word** → about **word**
        .replace(/([a-zA-ZáéíóúÁÉÍÓÚñÑ])\*\*([^*\n]+)\*\*/g, '$1 **$2**')
        // Cuatro o más asteriscos seguidos son un error del modelo (***bold
        // italic*** sí es válido y se respeta)
        .replace(/\*{4,}/g, '**')
        // Normalizar múltiples espacios (no saltos de línea)
        .replace(/[^\S\n]+/g, ' ')
        // Limpiar espacios al inicio/final de cada línea
        .split('\n').map((l) => l.trim()).join('\n')
        // Colapsar más de 2 saltos de línea consecutivos
        .replace(/\n{3,}/g, '\n\n')
        .trim();
}

// ─────────────────────────────────────────────────────────────────────────────
// DETECTORES DE LÍNEA
// ─────────────────────────────────────────────────────────────────────────────

// Viñetas: "- text", "• text", "* text"
//
// El guion admite NO llevar espacio detrás — Spacy escribe "-Are you a student?"
// más a menudo de lo que debería, y antes esa línea se quedaba como párrafo
// suelto con el guion pegado al texto. El asterisco sí lo exige, o "*to be* is a
// verb" al principio de línea se convertiría en viñeta.
export const BULLET_RE = /^(?:[-•]\s*|\*\s+)(.+)/;

// Lista numerada: "1. text"
export const NUMBERED_RE = /^(\d+)\.\s+(.+)/;

// ─────────────────────────────────────────────────────────────────────────────
// BLOCK PARSER — Divide el texto en bloques y los clasifica
// ─────────────────────────────────────────────────────────────────────────────
export function parseBlocks(text) {
    const lines = text.split('\n');
    const blocks = [];
    let currentList = null;
    let currentListType = null; // 'bullet' | 'numbered'
    let currentParagraph = [];

    function flushParagraph() {
        if (currentParagraph.length > 0) {
            blocks.push({ type: 'paragraph', lines: [...currentParagraph] });
            currentParagraph = [];
        }
    }

    function flushList() {
        if (currentList && currentList.length > 0) {
            blocks.push({ type: currentListType, items: [...currentList] });
            currentList = null;
            currentListType = null;
        }
    }

    for (let i = 0; i < lines.length; i++) {
        const raw = lines[i];
        const line = raw.trim();

        // Línea vacía → separador de bloques
        if (!line) {
            flushParagraph();
            flushList();
            continue;
        }

        // Lista numerada
        const numMatch = line.match(NUMBERED_RE);
        if (numMatch) {
            flushParagraph();
            if (currentListType !== 'numbered') { flushList(); currentList = []; currentListType = 'numbered'; }
            currentList.push(numMatch[2]);
            continue;
        }

        // Lista de viñetas
        const bulletMatch = line.match(BULLET_RE);
        if (bulletMatch) {
            flushParagraph();
            if (currentListType !== 'bullet') { flushList(); currentList = []; currentListType = 'bullet'; }
            currentList.push(bulletMatch[1]);
            continue;
        }

        // Línea normal
        flushList();
        currentParagraph.push(line);
    }

    flushParagraph();
    flushList();

    return blocks;
}
